/**
 * Amplitude Analytics Service
 * Provides initialization and user tracking functionality for Amplitude Browser SDK 2
 */

import * as amplitude from '@amplitude/analytics-browser';
import { AMPLITUDE } from '#/config/consts';

/**
 * Configuration for Amplitude SDK initialization
 * Includes all available autocapture events and meaningful tracking options
 */
const AMPLITUDE_CONFIG = {
  instanceName: AMPLITUDE.instanceName,
  serverZone: 'US' as const,
  autocapture: {
    attribution: true,
    pageViews: true,
    pageLeaves: true,
    sessions: true,
    sessionStart: true,
    formInteractions: true,
    fileDownloads: true,
    elementInteractions: true,
    pageUrlEnrichment: true,
    networkTracking: true,
    webVitals: true,
    frustrationInteractions: true,
  },
  // Session timeout in milliseconds (30 minutes)
  sessionTimeout: 30 * 60 * 1000,
  // Batch size for sending events
  batchSize: 30,
  // Flush interval in milliseconds (10 seconds)
  flushIntervalMillis: 10000,
  // Track default event properties
  trackingOptions: {
    ipAddress: true,
    locale: true,
    pageTitle: true,
  },
};

/**
 * Initialize Amplitude SDK with optional user ID
 * This should be called early in the app initialization, ideally in SessionProvider
 * @param userId - Optional user ID/email to set on initialization
 */
export const initializeAmplitude = (userId?: string): void => {
  try {
    if (!AMPLITUDE.apiKey) {
      console.warn(
        '[Amplitude] API key not configured. Analytics will not be tracked. ' +
          'Please set AMPLITUDE API KEY environment variable.'
      );
      return;
    }

    amplitude.init(AMPLITUDE.apiKey, userId, AMPLITUDE_CONFIG);
    console.debug('[Amplitude] SDK initialized successfully', {
      apiKey: AMPLITUDE.apiKey.substring(0, 8) + '****',
      userId,
      instanceName: AMPLITUDE_CONFIG.instanceName,
    });
  } catch (error) {
    console.error('[Amplitude] Initialization failed', error);
  }
};

/**
 * Set or update the current user ID
 * Call this after user authentication is complete
 * @param userId - User ID or email to identify the user
 */
export const setAmplitudeUserId = (userId: string | undefined): void => {
  if (!userId) {
    console.debug('[Amplitude] No user ID provided, skipping setUserId');
    return;
  }

  try {
    amplitude.setUserId(userId);
    console.debug('[Amplitude] User ID set', { userId: userId.substring(0, 8) + '****' });
  } catch (error) {
    console.error('[Amplitude] Failed to set user ID', error);
  }
};

/**
 * Reset the Amplitude session and clear user identification
 * Call this on logout to ensure analytics are properly reset
 */
export const resetAmplitude = (): void => {
  try {
    amplitude.reset();
    console.debug('[Amplitude] Session reset');
  } catch (error) {
    console.error('[Amplitude] Failed to reset session', error);
  }
};

/**
 * Track a custom event with optional properties
 * Use this for application-specific tracking beyond autocapture
 * @param eventName - Name of the event to track
 * @param eventProperties - Optional properties to attach to the event
 */
export const trackAmplitudeEvent = (
  eventName: string,
  eventProperties?: Record<string, unknown>
): void => {
  try {
    if (!eventName) {
      console.warn('[Amplitude] Event name is required');
      return;
    }

    amplitude.track(eventName, eventProperties);
    console.debug('[Amplitude] Event tracked', { eventName, properties: eventProperties });
  } catch (error) {
    console.error('[Amplitude] Failed to track event', { eventName, error });
  }
};

/**
 * Identify a user with custom properties
 * Use this to set user properties beyond just the user ID
 * @param userId - User ID to identify
 * @param userProperties - Optional properties to set for the user
 */
export const identifyAmplitudeUser = (
  userId: string,
  userProperties?: Record<string, unknown>
): void => {
  try {
    if (!userId) {
      console.warn('[Amplitude] User ID is required for identification');
      return;
    }

    const identify = new amplitude.Identify();

    // Set standard user properties
    if (userProperties) {
      Object.entries(userProperties).forEach(([key, value]) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        identify.set(key, value as any);
      });
    }

    amplitude.identify(identify);
    amplitude.setUserId(userId);
    console.debug('[Amplitude] User identified', { userId, properties: userProperties });
  } catch (error) {
    console.error('[Amplitude] Failed to identify user', error);
  }
};
